import { useCreateReportDataFields } from "@entities/reports/lib/hooks/customFields/useCreateReportDataFields";
import { useToggleReportDataField } from "@entities/reports/lib/hooks/customFields/useToggleReportDataField";
import { ToastService } from "@shared/lib/services";
import {
  IReportCustomFieledConfig,
  IReportDetail,
  ReportCustomFieldType,
} from "@shared/lib/types/reports";
import { Button } from "@shared/ui";
import { useEffect, useState } from "react";
import { useReportCustomFieldsForm } from "./lib/useReportCustomFieldsForm";
import styles from "./styles.module.scss";
import { ReportCustomFieldsForm } from "./ui/ReportCustomFieldsForm";
import { ReportCustomFieldsView } from "./ui/ReportCustomFieldsView";

type PanelState = "view" | "create";

export function CustomizeReportFields({
  onClose,
  reportId,
  report,
}: {
  onClose: () => void;
  reportId: string;
  report: IReportDetail["report"];
}) {
  const beCustomFields = report?.customFieldsV2 || [];

  const [selectedCustomFields, setSelectedCustomFields] = useState<
    IReportCustomFieledConfig[]
  >(report?.visibileCustomFieldsV2 || []);

  const [panel, setPanel] = useState<PanelState>("view");
  const [createSession, setCreateSession] = useState(0);

  const { createReportDataFields, isCreated, isCreating } =
    useCreateReportDataFields(reportId);
  const { isToggling, toggleDataField } = useToggleReportDataField(
    reportId,
    () => {
      ToastService.success("Fields configured successfully");
      onClose();
    }
  );

  const isCreateMode = panel === "create";
  const {
    fieldName,
    setFieldName,
    fieldType,
    setFieldType,
    fieldDefault,
    setFieldDefault,
    description,
    setDescription,
  } = useReportCustomFieldsForm({ isCreateMode, createSession });

  useEffect(() => {
    if (report) {
      setSelectedCustomFields(report.visibileCustomFieldsV2 || []);
    }
  }, [report]);

  useEffect(() => {
    if (isCreated) {
      ToastService.success("Field created successfully");
      setPanel("view");
    }
  }, [isCreated]);

  const handleFieldVisibility = (field: IReportCustomFieledConfig) => {
    const isVisible = selectedCustomFields.some((f) => f.key === field.key);
    if (isVisible) {
      setSelectedCustomFields((prev) =>
        prev.filter((f) => f.key !== field.key)
      );
    } else {
      setSelectedCustomFields((prev) => [...prev, field]);
    }
  };

  const getFieldVisibility = (field: IReportCustomFieledConfig): boolean => {
    return selectedCustomFields.some((f) => f.key === field.key);
  };

  const handleCreateClick = () => {
    setCreateSession((n) => n + 1);
    setPanel("create");
  };

  const handleCancelCreate = () => {
    setPanel("view");
  };

  const handleSubmitCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!fieldName.trim()) {
      ToastService.error("Field name is required");
      return;
    }

    const keyBase = fieldName
      .toLowerCase()
      .replace(/\s+/g, "_")
      .replace(/[ .]/g, "_");
    let resolvedDefault: string | number | undefined;
    if (fieldType === ReportCustomFieldType.NUMBER) {
      if (fieldDefault === undefined || fieldDefault === "") {
        resolvedDefault = undefined;
      } else {
        const n = Number(fieldDefault);
        if (Number.isNaN(n)) {
          ToastService.error("Default value must be a valid number");
          return;
        }
        resolvedDefault = n;
      }
    } else {
      const s = fieldDefault as string | undefined;
      resolvedDefault = s === "" || s === undefined ? undefined : s;
    }

    const customFieldsV2: IReportCustomFieledConfig = {
      key: `${keyBase}_${Date.now()}`,
      isSpecialField: true,
      category: "Reporting",
      type: fieldType,
      displayName: fieldName.trim(),
      defaultValue: resolvedDefault,
      description: description.trim() || undefined,
    };

    createReportDataFields({ reportId, customFieldsV2 });
  };

  return (
    <div className={styles.container}>
      {panel === "view" ? (
        <>
          <ReportCustomFieldsView
            fields={beCustomFields}
            onCreateClick={handleCreateClick}
            onToggleVisibility={handleFieldVisibility}
            getFieldVisibility={getFieldVisibility}
            isToggling={isToggling}
          />
          <div className={styles.configFooter}>
            <Button
              type="default"
              onClick={onClose}
              className={styles.configFooterButton}
            >
              Close
            </Button>
            <Button
              type="primary"
              loading={isToggling}
              onClick={() => {
                toggleDataField({
                  reportId,
                  visibileCustomFieldsV2: [...selectedCustomFields],
                });
              }}
              className={styles.configFooterButton}
            >
              Save
            </Button>
          </div>
        </>
      ) : (
        <ReportCustomFieldsForm
          fieldName={fieldName}
          fieldType={fieldType}
          fieldDefault={fieldDefault}
          description={description}
          isSubmitting={isCreating}
          onFieldNameChange={setFieldName}
          onFieldTypeChange={(type) => {
            setFieldType(type);
            setFieldDefault(undefined);
          }}
          onFieldDefaultChange={setFieldDefault}
          onDescriptionChange={setDescription}
          onSubmit={handleSubmitCreate}
          onCancel={handleCancelCreate}
        />
      )}
    </div>
  );
}
